import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import Modal from "react-modal";
import "./QueryManagementSystem.css";
import { useAuth } from "../AuthContext";
import debounce from "lodash.debounce";
import Papa from "papaparse"; // Add this import for parsing CSV files

Modal.setAppElement("#root");

const QueryManagementSystem = () => {
  const { mobile } = useAuth();
  const [queries, setQueries] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [newQuery, setNewQuery] = useState({
    name: "",
    area: "",
    mobile_number_1: "",
    mobile_number_2: "",
    remarks: "",
    status: "",
  });
  const [areas, setAreas] = useState([]);
  const [filteredAreas, setFilteredAreas] = useState([]);
  const [editBuffer, setEditBuffer] = useState({});
  const [popupMessage, setPopupMessage] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [employeeNames, setEmployeeNames] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [filterCounts, setFilterCounts] = useState({});
  const [employeeQueryCounts, setEmployeeQueryCounts] = useState({});
  const [filterStatus, setFilterStatus] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [duplicateQuery, setDuplicateQuery] = useState(null);
  const [showCsvUploadModal, setShowCsvUploadModal] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [showAllocateModal, setShowAllocateModal] = useState(false);
  const [selectedAllocationEmployees, setSelectedAllocationEmployees] =
    useState([]);
  const [unallocatedQueriesCount, setUnallocatedQueriesCount] = useState(0);
  const [allocatingQueryCount, setAllocatingQueryCount] = useState(0);

  const statusOptions = [
    "Not Pick",
    "List Sent",
    "Interested",
    "Not Interested",
    "Converted",
    "Not Relevant",
    "Unfollowed",
    "Area Not Vacant",
    "Third Party",
    "Incoming NA",
    "Switched Off",
    "Credit Basis Only",
  ];

  const filterOptions = [
    "Not Pick",
    "List Sent",
    "Interested",
    "Converted",
    "Unfollowed",
    "Switched Off",
    "Area Not Vacant",
  ];

  const salesEmployees = [
    "Aarti",
    "Ajay",
    "Akshay",
    "Anil",
    "Chandni",
    "Dolly",
    "Gobind",
    "Jyoti",
    "Komal",
    "Manpreet",
    "Neetu",
    "Pankaj Bhatt",
    "Pankaj Rawat",
    "Prabhjot",
    "Priya",
    "Sanju",
    "Sarita",
    "Satyam",
    "Shreya",
    "Suman",
    "Tarun",
    "Tanu",
    "Usha",
    "Nobody",
  ];

  const handleCsvUploadChange = (e) => {
    setCsvFile(e.target.files[0]);
  };

  const handleQueryCountChange = (e) => {
    setAllocatingQueryCount(Number(e.target.value));
  };

  const calculateRemainingQueries = () => {
    const allocatingCount =
      selectedAllocationEmployees.length * allocatingQueryCount;
    return Math.max(0, unallocatedQueriesCount - allocatingCount);
  };

  const handleCsvUploadSubmit = async () => {
    if (!csvFile) {
      alert("Please select a CSV file first.");
      return;
    }

    Papa.parse(csvFile, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        try {
          console.log(results.data);
          await axios.post(
            `${process.env.REACT_APP_API_URL}/api/upload-queries`,
            results.data
          );
          setShowPopup(true);
          setPopupMessage("CSV uploaded successfully");
          setShowCsvUploadModal(false);
          fetchQueries();
        } catch (error) {
          console.error("Error uploading CSV:", error);
          setPopupMessage("Failed to upload CSV");
          setShowPopup(true);
        }
      },
    });
  };

  const fetchQueries = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/queries`,
        {
          params: { searchTerm, mobile },
        }
      );
      const sortedQueries = response.data.sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      setQueries(sortedQueries);

      const unallocatedCount = sortedQueries.filter(
        (query) => query.employee_name.toLowerCase() === "nobody"
      ).length;
      setUnallocatedQueriesCount(unallocatedCount);

      // Calculate filter counts
      const counts = sortedQueries.reduce((acc, query) => {
        acc[query.status] = (acc[query.status] || 0) + 1;
        return acc;
      }, {});
      setFilterCounts(counts);

      if (mobile === "9041677932") {
        const uniqueEmployeeNames = [
          ...new Set(sortedQueries.map((query) => query.employee_name)),
        ];
        uniqueEmployeeNames.sort(); // Sort employee names in ascending order
        setEmployeeNames(uniqueEmployeeNames);

        // Calculate today's counts and total "Unfollowed" counts for employee filters
        const today = new Date().toISOString().slice(0, 10);
        const employeeCounts = sortedQueries.reduce((acc, query) => {
          if (query.created_at.slice(0, 10) === today) {
            acc[query.employee_name] = acc[query.employee_name] || {
              today: 0,
              unfollowed: 0,
            };
            acc[query.employee_name].today += 1;
          }
          if (query.status === "Unfollowed") {
            acc[query.employee_name] = acc[query.employee_name] || {
              today: 0,
              unfollowed: 0,
            };
            acc[query.employee_name].unfollowed += 1;
          }
          return acc;
        }, {});
        setEmployeeQueryCounts(employeeCounts);
      }
    } catch (error) {
      console.error("Error fetching queries:", error);
    }
  };

  const openAllocateModal = () => {
    setShowAllocateModal(true);
    setSelectedAllocationEmployees([]);
  };

  const closeAllocateModal = () => {
    setShowAllocateModal(false);
  };

  // Adjust the handleAllocationEmployeeChange function to update allocatedQueries
  const handleAllocationEmployeeChange = (e) => {
    const { value, checked } = e.target;
    setSelectedAllocationEmployees((prevSelectedAllocationEmployees) =>
      checked
        ? [...prevSelectedAllocationEmployees, value]
        : prevSelectedAllocationEmployees.filter((name) => name !== value)
    );
  };

  const handleAllocateFormSubmit = async (event) => {
    event.preventDefault();
    const queryCount = document.getElementById("queryCount").value;

    // Lowercase all selected employee names
    const selectedEmployeesLowercased = selectedAllocationEmployees.map(
      (employee) => employee.toLowerCase()
    );

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/allocate-queries`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            queryCount,
            selectedEmployees: selectedEmployeesLowercased,
          }),
        }
      );

      const result = await response.json();
      if (response.ok) {
        alert(result.message);
        closeAllocateModal();
        fetchQueries();
      } else {
        alert(result.message);
      }
    } catch (error) {
      console.error("Error allocating queries:", error);
      alert("Error allocating queries");
    }
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (password === "5037") {
      setIsAuthenticated(true);
    } else {
      alert("Incorrect password");
    }
  };

  useEffect(() => {
    fetchQueries();
    const intervalId = setInterval(fetchQueries, 60000); // 60 seconds
    return () => clearInterval(intervalId); // Clear interval on component unmount
  }, [searchTerm]);

  const handleEmployeeChange = async (e, id) => {
    const newEmployeeName = e.target.value;
    try {
      // Update the state locally for immediate feedback
      setQueries((prevQueries) =>
        prevQueries.map((query) =>
          query.id === id ? { ...query, employee_name: newEmployeeName } : query
        )
      );

      await axios.patch(`${process.env.REACT_APP_API_URL}/api/queries/${id}`, {
        name: "employee_name",
        value: newEmployeeName,
      });
      showPopupMessage("Employee reassigned successfully");
      fetchQueries(); // Refetch to ensure state is in sync with backend
    } catch (error) {
      console.error("Error reassigning employee:", error);
    }
  };

  const handleEmployeeFilterChange = (e) => {
    const { value, checked } = e.target;
    setSelectedEmployees((prevSelectedEmployees) =>
      checked
        ? [...prevSelectedEmployees, value]
        : prevSelectedEmployees.filter((name) => name !== value)
    );
  };

  useEffect(() => {
    fetchQueries();
  }, [searchTerm]);

  useEffect(() => {
    const fetchAreas = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/cities`
        );
        setAreas(response.data);
      } catch (error) {
        console.error("Error fetching areas:", error);
      }
    };

    fetchAreas();
  }, []);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleNewQueryChange = (e) => {
    const { name, value } = e.target;

    if (name === "mobile_number_1") {
      if (!/^\d{0,10}$/.test(value)) {
        return; // Do nothing if value is not a digit or is more than 10 digits
      }

      // Check if the mobile number already exists in the queries
      const duplicate = queries.find(
        (query) => query.mobile_number_1 === value
      );

      if (duplicate) {
        setDuplicateQuery({
          date: formatDate(duplicate.created_at),
          employee: duplicate.employee_name,
        });
      } else {
        setDuplicateQuery(null);
      }
    }

    setNewQuery({ ...newQuery, [name]: value });
  };

  const handleAreaChange = (e) => {
    const value = e.target.value;
    setNewQuery({ ...newQuery, area: value });
    debounceAreaChange(value);
  };

  // Memoize the filtered areas to improve performance
  const filteredAreasMemo = useMemo(() => {
    const value = newQuery.area.toLowerCase();
    return areas
      .filter((area) => area.city.toLowerCase().includes(value))
      .slice(0, 10); // Limit to top 10 matches
  }, [areas, newQuery.area]);

  // Debounced function to handle area change
  const debounceAreaChange = useCallback(
    debounce((value) => {
      setFilteredAreas(
        areas
          .filter((area) =>
            area.city.toLowerCase().includes(value.toLowerCase())
          )
          .slice(0, 10)
      ); // Limit to top 10 matches
    }, 300),
    [areas]
  );

  const handleAreaSelect = (e) => {
    const value = e.target.value;
    setNewQuery({ ...newQuery, area: value });
  };

  const handleEditAreaChange = (e, id) => {
    const { name, value } = e.target;
    setQueries((prevQueries) =>
      prevQueries.map((query) =>
        query.id === id ? { ...query, [name]: value } : query
      )
    );

    setEditBuffer((prev) => ({
      ...prev,
      [id]: { ...prev[id], [name]: value },
    }));
  };

  const handleEditAreaBlur = async (e, id) => {
    const { name } = e.target;
    const value = e.target.value || ""; // Ensure value is an empty string if field is empty

    try {
      await axios.patch(`${process.env.REACT_APP_API_URL}/api/queries/${id}`, {
        name,
        value,
      });
      setQueries((prevQueries) =>
        prevQueries.map((query) =>
          query.id === id ? { ...query, [name]: value } : query
        )
      );
      showPopupMessage(`Field ${name} updated successfully`);
    } catch (error) {
      console.error("Error updating query:", error);
    }
    setEditBuffer((prev) => {
      const { [id]: _, ...rest } = prev;
      return rest;
    });
  };

  const handleNewQuerySubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/queries`, {
        ...newQuery,
        mobile,
        status: mobile === "9041677932" ? "Unfollowed" : newQuery.status,
      });
      showPopupMessage("Query Posted successfully");
      fetchQueries();
      if (mobile !== "9041677932") {
        setShowModal(false);
      }
      if (mobile !== "9041677932") {
        setNewQuery({
          name: "",
          area: "",
          mobile_number_1: "",
          mobile_number_2: "",
          remarks: "",
          status: "",
        });
      } else {
        setNewQuery({
          name: "",
          area: "",
          mobile_number_1: "",
          mobile_number_2: "",
          remarks: "",
          status: "Unfollowed",
          employee_name: mobile === "9041677932" ? "nobody" : "",
        });
      }
    } catch (error) {
      console.error("Error creating new query:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditChange = (e, id) => {
    const { name, value } = e.target;

    if (name === "mobile_number_1") {
      return; // Ignore changes to mobile_number_1
    }

    setQueries((prevQueries) =>
      prevQueries.map((query) =>
        query.id === id ? { ...query, [name]: value } : query
      )
    );

    setEditBuffer((prev) => ({
      ...prev,
      [id]: { ...prev[id], [name]: value },
    }));

    if (name === "status") {
      // Call backend immediately for status changes
      handleStatusChange(id, value);
    }
  };

  const handleStatusChange = async (id, value) => {
    try {
      await axios.patch(`${process.env.REACT_APP_API_URL}/api/queries/${id}`, {
        name: "status",
        value,
      });
      showPopupMessage("Status updated successfully");
      fetchQueries();
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  const handleEditBlur = async (e, id) => {
    const { name } = e.target;
    const value = e.target.value || ""; // Ensure value is an empty string if field is empty

    if (name === "mobile_number_1") {
      return; // Ignore blur event for mobile_number_1
    }

    try {
      await axios.patch(`${process.env.REACT_APP_API_URL}/api/queries/${id}`, {
        name,
        value,
      });
      setQueries((prevQueries) =>
        prevQueries.map((query) =>
          query.id === id ? { ...query, [name]: value } : query
        )
      );
      showPopupMessage(`Field ${name} updated successfully`);
    } catch (error) {
      console.error("Error updating query:", error);
    }
    setEditBuffer((prev) => {
      const { [id]: _, ...rest } = prev;
      return rest;
    });
  };

  const showPopupMessage = (message) => {
    setPopupMessage(message);
    setShowPopup(true);
    setTimeout(() => {
      setShowPopup(false);
    }, 1000);
  };

  const handleDeleteQuery = async (id) => {
    const isConfirmed = window.confirm(
      "Are you sure you want to delete this query?"
    );
    if (!isConfirmed) {
      return;
    }

    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/queries/${id}`);
      fetchQueries();
      showPopupMessage("Query deleted successfully");
    } catch (error) {
      console.error("Error deleting query:", error);
    }
  };

  const filteredQueries = queries.filter((query) => {
    const matchesStatus = !filterStatus || query.status === filterStatus;
    const matchesEmployee =
      selectedEmployees.length === 0 ||
      selectedEmployees.includes(query.employee_name);
    return matchesStatus && matchesEmployee;
  });

  const renderEmployeeFilters = () => (
    <div className="employee-filter-container">
      {employeeNames.map((employeeName) => (
        <label key={employeeName} className="employee-filter-label">
          <input
            type="checkbox"
            value={employeeName}
            checked={selectedEmployees.includes(employeeName)}
            onChange={handleEmployeeFilterChange}
          />
          {employeeName} ({employeeQueryCounts[employeeName]?.today || 0}) [
          {employeeQueryCounts[employeeName]?.unfollowed || 0}]
        </label>
      ))}
    </div>
  );

  const renderFilterContainer = () => (
    <div className="filter-container">
      {filterOptions.map((status) => (
        <label key={status}>
          <input
            type="checkbox"
            checked={filterStatus === status}
            onChange={(e) => setFilterStatus(e.target.checked ? status : "")}
          />
          {status} ({filterCounts[status] || 0})
        </label>
      ))}
    </div>
  );
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${day}-${month}-${year} ${hours}:${minutes}`;
  };

  const getRowStyle = (status) => {
    switch (status) {
      case "Not Pick":
        return { backgroundColor: "#ffcccc" };
      case "Not Relevant":
        return { backgroundColor: "#ff69b4" };
      case "List Sent":
        return { backgroundColor: "#ffffcc" };
      case "Interested":
        return { backgroundColor: "#ccffcc" };
      case "Converted":
        return { backgroundColor: "#66ff66" };
      default:
        return {};
    }
  };

  const renderQueriesTable = () => (
    <table>
      <thead>
        <tr>
          <th style={{ width: "8%" }}>Date</th>
          <th style={{ width: "12%" }}>Name</th>
          <th style={{ width: "10%" }}>Area</th>
          <th style={{ width: "10%" }}>Mobile 1</th>
          <th style={{ width: "10%" }}>Mobile 2</th>
          <th style={{ width: "25%" }}>Remarks</th>
          <th style={{ width: "10%" }}>Employee Name</th>
          <th style={{ width: "10%" }}>Status</th>
          {mobile === "9041677932" && <th style={{ width: "10%" }}>Actions</th>}
        </tr>
      </thead>
      <tbody>
        {filteredQueries.map((query) => (
          <tr key={query.id} style={getRowStyle(query.status)}>
            <td style={{ width: "8%" }}>{formatDate(query.created_at)}</td>
            <td style={{ width: "12%" }}>
              <input
                type="text"
                name="name"
                value={query.name}
                onChange={(e) => handleEditChange(e, query.id)}
                onBlur={(e) => handleEditBlur(e, query.id)}
              />
            </td>
            <td style={{ width: "10%" }}>
              <input
                type="text"
                name="area"
                value={query.area}
                onChange={(e) => handleEditAreaChange(e, query.id)}
                onBlur={(e) => handleEditAreaBlur(e, query.id)}
                list={`area-options-${query.id}`}
              />
              <datalist id={`area-options-${query.id}`}>
                {filteredAreas.map((area, index) => (
                  <option key={index} value={`${area.city}`} />
                ))}
              </datalist>
            </td>
            <td style={{ width: "10%" }}>
              <input
                type="text"
                name="mobile_number_1"
                value={query.mobile_number_1}
                onChange={(e) => handleEditChange(e, query.id)}
                onBlur={(e) => handleEditBlur(e, query.id)}
                readOnly
              />
            </td>
            <td style={{ width: "10%" }}>
              <input
                type="text"
                name="mobile_number_2"
                value={query.mobile_number_2}
                onChange={(e) => handleEditChange(e, query.id)}
                onBlur={(e) => handleEditBlur(e, query.id)}
              />
            </td>
            <td style={{ width: "25%" }}>
              <input
                type="text"
                name="remarks"
                value={query.remarks}
                onChange={(e) => handleEditChange(e, query.id)}
                onBlur={(e) => handleEditBlur(e, query.id)}
              />
            </td>
            <td style={{ width: "10%" }}>
              {mobile === "9041677932" ? (
                <select
                  name="employee_name"
                  value={query.employee_name}
                  onChange={(e) => handleEmployeeChange(e, query.id)}
                >
                  {salesEmployees.map((employee) => (
                    <option key={employee} value={employee.toLowerCase()}>
                      {employee.toLowerCase()}
                    </option>
                  ))}
                </select>
              ) : (
                query.employee_name
              )}
            </td>
            <td style={{ width: "10%" }}>
              <select
                name="status"
                value={query.status}
                onChange={(e) => handleEditChange(e, query.id)}
                onBlur={(e) => handleEditBlur(e, query.id)}
              >
                {statusOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </td>
            {mobile === "9041677932" && (
              <td style={{ width: "10%" }}>
                <button onClick={() => handleDeleteQuery(query.id)}>
                  Delete
                </button>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );

  if (mobile === "9041677932" && !isAuthenticated) {
    return (
      <div className="password-protect">
        <form onSubmit={handlePasswordSubmit}>
          <label htmlFor="password">Enter Password:</label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <button type="submit">Submit</button>
        </form>
      </div>
    );
  }

  return (
    <>
      <div className="query-page">
        <div className="top-bar">
          <div className="search-bar">
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>

          {renderFilterContainer()}
          <button onClick={() => setShowModal(true)}>Add Query</button>
          {mobile === "9041677932" && (
            <>
              <button onClick={() => setShowCsvUploadModal(true)}>
                Add Multiple Queries
              </button>
              <button onClick={openAllocateModal}>Allocate Queries</button>
            </>
          )}

          <Modal
            isOpen={showCsvUploadModal}
            onRequestClose={() => setShowCsvUploadModal(false)}
            className="Modal__Content"
            overlayClassName="Modal__Overlay"
          >
            <span
              className="Modal__CloseButton"
              onClick={() => setShowCsvUploadModal(false)}
            >
              &times;
            </span>
            <h2>Upload CSV</h2>
            <input type="file" accept=".csv" onChange={handleCsvUploadChange} />
            <button onClick={handleCsvUploadSubmit}>Upload</button>
          </Modal>
        </div>
        {mobile === "9041677932" && renderEmployeeFilters()}
        {renderQueriesTable()}
        {showPopup && <div className="popup">{popupMessage}</div>}
        <Modal
          isOpen={showModal}
          onRequestClose={() => setShowModal(false)}
          className="Modal__Content"
          overlayClassName="Modal__Overlay"
        >
          <span
            className="Modal__CloseButton"
            onClick={() => setShowModal(false)}
          >
            &times;
          </span>
          <h2>New Query</h2>
          <form onSubmit={handleNewQuerySubmit}>
            <input
              type="text"
              name="name"
              value={newQuery.name}
              onChange={handleNewQueryChange}
              placeholder="Name"
            />
            <input
              type="text"
              name="mobile_number_1"
              value={newQuery.mobile_number_1}
              onChange={handleNewQueryChange}
              placeholder="Mobile Number 1"
              required
              pattern="\d{10}"
              title="Please enter exactly 10 digits"
            />
            {duplicateQuery && (
              <div style={{ color: "red" }}>
                This query already posted on {duplicateQuery.date} and assigned
                to {duplicateQuery.employee}.
              </div>
            )}
            <input
              type="text"
              name="mobile_number_2"
              value={newQuery.mobile_number_2}
              onChange={handleNewQueryChange}
              placeholder="Mobile Number 2"
              pattern="\d{10}"
              title="Please enter exactly 10 digits"
            />
            <input
              type="text"
              name="area"
              value={newQuery.area}
              onChange={handleAreaChange}
              onBlur={handleAreaSelect}
              placeholder="Area (City)"
              list="area-options"
            />
            <datalist id="area-options">
              {filteredAreasMemo.map((area, index) => (
                <option key={index} value={`${area.city}`} />
              ))}
            </datalist>
            <select
              name="status"
              value={newQuery.status}
              onChange={handleNewQueryChange}
              required
            >
              <option value="" disabled>
                Select Status
              </option>
              {statusOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>

            <textarea
              name="remarks"
              value={newQuery.remarks}
              onChange={handleNewQueryChange}
              placeholder="Remarks"
            />
            <button type="submit" disabled={isSubmitting}>
              Submit
            </button>
          </form>
        </Modal>
      </div>

      <Modal
        isOpen={showAllocateModal}
        onRequestClose={closeAllocateModal}
        className="Modal__Content"
        overlayClassName="Modal__Overlay"
      >
        <span className="Modal__CloseButton" onClick={closeAllocateModal}>
          &times;
        </span>
        <h2>Allocate Queries ({calculateRemainingQueries()} unallocated)</h2>
        <form id="allocateForm">
          <div className="employee-filter-container-1">
            {salesEmployees.map((employee) => (
              <label key={employee} className="employee-filter-label">
                <input
                  type="checkbox"
                  value={employee}
                  checked={selectedAllocationEmployees.includes(employee)}
                  onChange={handleAllocationEmployeeChange}
                />
                {employee}
              </label>
            ))}
          </div>
          <input
            type="number"
            id="queryCount"
            name="queryCount"
            placeholder="Number of queries"
            required
            onChange={handleQueryCountChange}
            value={allocatingQueryCount}
          />
          <button type="submit" onClick={handleAllocateFormSubmit}>
            Allocate
          </button>
        </form>
      </Modal>
    </>
  );
};

export default QueryManagementSystem;
